import { useEffect, useState } from 'react';
import { config } from '../../../config';
import { tasksApi } from '../tasks.api';
import type { Meta } from '../tasks.types';

/**
 * The pickers and the caller's permissions, fetched once per login.
 *
 * Kept with the token it was fetched under. Signing out and in as someone else does not
 * reload the page, so a cache held for the whole page load used to hand the next user the
 * previous one's permissions — an admin signing in after an employee got no "New task"
 * button until the browser was cleared. A different token now means a fresh fetch.
 */
let cached: Promise<Meta> | null = null;
let cachedFor: string | null = null;

const currentToken = () => {
  try {
    return localStorage.getItem(config.AUTH_LS_KEYS.TOKEN);
  } catch {
    return null;
  }
};

export function useTaskMeta(): Meta | null {
  const [meta, setMeta] = useState<Meta | null>(null);
  useEffect(() => {
    let alive = true;
    const token = currentToken();
    if (!cached || cachedFor !== token) {
      cachedFor = token;
      cached = tasksApi.meta().catch((e) => {
        cached = null;
        cachedFor = null;
        throw e;
      });
    }
    cached.then((m) => alive && setMeta(m)).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return meta;
}

/** Forget the cached pickers and permissions (e.g. after logout). */
export function resetTaskMeta() {
  cached = null;
  cachedFor = null;
}
